import React from "react";
import { Box, Typography } from "@mui/material";
import { TIPOS_DOCUMENTOS, TIPOS_MENSAJES } from "../../../Constants";
import Texto from "../../chatCobranza/Texto";
import Image from "../../chatCobranza/Image";
import Video from "../../chatCobranza/Video";
import Document from "../../chatCobranza/Document";
import Audio from "../../chatCobranza/Audio";
import Interactive from "../../chatCobranza/Interactive";

/**
 * Normaliza los botones de un mensaje interactivo: el backend puede mandar un array
 * o un string separado por "/". Evita llamar `.map` sobre un string.
 * @param {unknown} buttons
 * @returns {string[]}
 */
const normalizeButtons = (buttons) => {
  if (Array.isArray(buttons)) return buttons;
  return String(buttons || "")
    .split("/")
    .map((b) => b.trim())
    .filter(Boolean);
};

/**
 * Despachador de burbujas: mapea cada mensaje a su componente según `type`,
 * reutilizando las burbujas de `chatCobranza`. La dirección (enviado/recibido) la
 * decide el contenedor con el predicado `isOutgoing`.
 *
 * @param {{
 *   messages: Array<{ uuid?: string, type?: string, message?: string, buttons?: unknown, senderCellphone?: string, timeSent?: string }>,
 *   isOutgoing: (message: object) => boolean,
 *   fetchMediaUrl: (key: string, setUrl: (url: string) => void, ext?: string) => void,
 *   onMediaClick?: (src: string, mediaType: "image" | "video") => void,
 * }} props
 */
export default function MessageThread({
  messages,
  isOutgoing,
  fetchMediaUrl,
  onMediaClick,
}) {
  if (!messages || messages.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Typography color="text.secondary">
          No hay mensajes en esta conversación
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {messages.map((item, index) => {
        const key = item.uuid || `msg-${index}`;
        const tipos = isOutgoing(item)
          ? TIPOS_MENSAJES.enviado
          : TIPOS_MENSAJES.recibido;
        const times = item.timeSent;

        switch (item.type) {
          case TIPOS_DOCUMENTOS.text:
            return <Texto key={key} texto={item.message} tipos={tipos} times={times} />;
          case TIPOS_DOCUMENTOS.image:
            return (
              <Image
                key={key}
                imageKey={item.message}
                tipos={tipos}
                times={times}
                fetchMediaUrl={fetchMediaUrl}
                onClick={() => onMediaClick?.(item.message, "image")}
              />
            );
          case TIPOS_DOCUMENTOS.video:
            return (
              <Video
                key={key}
                videKey={item.message}
                tipos={tipos}
                times={times}
                fetchMediaUrl={fetchMediaUrl}
                onClick={(url) => onMediaClick?.(url, "video")}
              />
            );
          case TIPOS_DOCUMENTOS.document:
            return (
              <Document
                key={key}
                document={item.message}
                tipos={tipos}
                times={times}
                fetchMediaUrl={fetchMediaUrl}
              />
            );
          case TIPOS_DOCUMENTOS.audio:
            return (
              <Audio
                key={key}
                audioKey={item.message}
                tipos={tipos}
                times={times}
                fetchMediaUrl={fetchMediaUrl}
              />
            );
          case TIPOS_DOCUMENTOS.interactive:
            return (
              <Interactive
                key={key}
                texto={item.message}
                content={normalizeButtons(item.buttons)}
                tipos={tipos}
                times={times}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
}
